import { IconName } from '@wui/common-library'
import { Component, DefineComponent, ref, Ref } from 'vue'

import { isCallQueuesSettingsVisible } from '@/entities/queue-service'

import { useAsyncSettingsComponents } from '@/shared/composables/router'
import { UserRoles } from '@/shared/types/router'
import { FeaturesNames } from '@/shared/types/settings'

const {
  AudiofilesManagement,
  IncomingCallHandling,
  BlackWhiteList,
  QueueServices,
  FollowMe,
  SpeedDial,
  IvrPage,
  VoiceMailForwarding,
  SubscriberPickUp,
  NotificationSettings,
  SubscriberAliases,
  ServicesHandle,
  HandleServicesPackages,
  PinPasswordInput,
  CLIR,
  AlarmClock,
  ASideMenuSettings,
  DesktopApplicationSettings,
  TerminalsList,
  EquipmentList,
  SessionsList,
  LanguageSettings,
  ThemeSettings,
  ChatSettings,
  ContactGroupsSettings,
} = useAsyncSettingsComponents()

export enum SettingsNames {
  account = 'account',
  general = 'generalSettings',
  confidentiality = 'confidentiality',
  soundAndVideo = 'soundAndVideoSettings',
  calls = 'callsSettings',
  chats = 'chatsSettings',
  notifications = 'notificationsSettings',
  contacts = 'contactsSettings',
  palette = 'paletteSettings',
  darkMode = 'darkMode',
  terminals = 'terminalsSettings',
  about = 'aboutApp',
  antiCaller = 'CLIR',
  interface = 'interface',
}

export interface SubLink {
  name: string,
  path: string,
  component: DefineComponent | Component,
  isFeatureEnabled: Ref<boolean>,
  roles?: UserRoles[],
  isTauriRoute? : boolean,
}

export interface Link {
  path: string;
  icon: IconName;
  roles: UserRoles[];
  features: FeaturesNames[];
  subLinks?: SubLink[];
  isNeedWebSoftphoneEnabled?: boolean;
}

export const links = new Map<SettingsNames, Link>()
links.set(SettingsNames.account, {
  path: '/settings/account',
  icon: 'userM',
  roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  features: [],
})
links.set(SettingsNames.confidentiality, {
  path: '/settings/confidentiality',
  icon: 'lockM',
  roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  features: [],
  subLinks: [
    {
      name: 'pinPasswordInput',
      path: '/settings/confidentiality/pin-password-page',
      component: PinPasswordInput,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
    },
    {
      name: SettingsNames.antiCaller,
      path: '/settings/confidentiality/c-l-i-r',
      component: CLIR,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
    },
  ],
})
links.set(SettingsNames.general, {
  path: '/settings/general-settings',
  icon: 'gearM',
  roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  features: [FeaturesNames.ACCESS_GROUP_MANAGEMENT, FeaturesNames.DEFAULT],
  subLinks: [
    {
      name: 'subscriberAliases',
      path: '/settings/general-settings/subscriber-aliases',
      component: SubscriberAliases,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
    },
    {
      name: 'servicesHandle',
      path: '/settings/general-settings/services-handle',
      component: ServicesHandle,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
    },
    {
      name: 'handleServicesPackages',
      path: '/settings/general-settings/handle-services-packages',
      component: HandleServicesPackages,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
    },
    {
      name: 'desktopApplicationSettings',
      path: '/settings/general-settings/desktop-settings',
      component: DesktopApplicationSettings,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
      isTauriRoute: true,
    },
    {
      name: 'handleContactGroups',
      path: '/settings/general-settings/contact-groups',
      component: ContactGroupsSettings,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER],
    },
  ],
})
links.set(SettingsNames.soundAndVideo, {
  path: '/settings/sound-and-video',
  icon: 'micM',
  features: [],
  roles: [UserRoles.SUBSCRIBER],
  isNeedWebSoftphoneEnabled: true,
})
links.set(SettingsNames.calls, {
  path: '/settings/calls/',
  icon: 'phoneM',
  roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  features: [
    FeaturesNames.CALL_WAITING,
    FeaturesNames.FORWARD,
    FeaturesNames.BLACK_WHITE_LIST,
    FeaturesNames.FOLLOW_ME,
    FeaturesNames.SPEED_DIAL,
    FeaturesNames.IVR,
    FeaturesNames.VOICEMAIL_FORWARDING,
    FeaturesNames.CLIR,
    FeaturesNames.CALLBACK_ORDER,
  ],
  subLinks: [{
    name: 'incomingCallHandling',
    path: '/settings/calls/incoming-call-handling',
    component: IncomingCallHandling,
    isFeatureEnabled: ref(true),
    roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  },
  {
    name: 'blackWhiteList',
    path: '/settings/calls/black-white-list',
    component: BlackWhiteList,
    isFeatureEnabled: ref(true),
    roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  },
  {
    name: 'followMe',
    path: '/settings/calls/follow-me',
    component: FollowMe,
    isFeatureEnabled: ref(true),
    roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  },
  {
    name: 'speedDial',
    path: '/settings/calls/speed-dial',
    component: SpeedDial,
    isFeatureEnabled: ref(true),
    roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  },
  {
    name: 'IVR',
    path: '/settings/calls/ivr',
    component: IvrPage,
    isFeatureEnabled: ref(true),
    roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  },
  {
    name: 'voiceMailForwarding',
    path: '/settings/calls/voice-mail-forwarding',
    component: VoiceMailForwarding,
    isFeatureEnabled: ref(true),
    roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  },
  {
    name: 'subscriberPickUp',
    path: '/settings/calls/subscriber-pick-up',
    component: SubscriberPickUp,
    isFeatureEnabled: ref(true),
    roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  },
  {
    name: 'callQueues',
    path: '/settings/calls/queue-services',
    component: QueueServices,
    isFeatureEnabled: isCallQueuesSettingsVisible,
    roles: [UserRoles.SUBSCRIBER],
  },
  ],
})
links.set(SettingsNames.terminals, {
  path: '/settings/terminals',
  icon: 'layoutTilesM',
  roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  features: [FeaturesNames.BLF, FeaturesNames.PERSONAL_WEB_CABINET_ACCESS],
  subLinks: [
    {
      name: 'terminalsList',
      path: '/settings/terminals/terminals-list',
      component: TerminalsList,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
    },
    {
      name: 'devices',
      path: '/settings/terminals/equipment',
      component: EquipmentList,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
    },
    {
      name: 'authorizedSessions',
      path: '/settings/terminals/sessions',
      component: SessionsList,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
    },
  ],
})
// links.set(SettingsNames.chats, { path: '/settings/chats', icon: 'chat' })
// links.set(SettingsNames.notifications, { path: '/settings/notifications', icon: 'notifications' })
links.set(SettingsNames.notifications, {
  path: '/settings/notifications/',
  icon: 'bellM',
  roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  features: [],
  subLinks: [
    {
      name: 'notificationSettings',
      path: '/settings/notifications/notifications-settings',
      component: NotificationSettings,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
    },
    {
      name: 'alarmClock',
      path: '/settings/notifications/alarm-schedule',
      component: AlarmClock,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
    }, {
      name: 'audiofilesManagement',
      path: '/settings/notifications/audio-file-settings',
      component: AudiofilesManagement,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
    }],
})

// links.set(SettingsNames.contacts, { path: '/settings/contacts', icon: 'contactsOutlined' })
// links.set(SettingsNames.palette, { path: '/settings/palette', icon: 'palette' })
// links.set(SettingsNames.darkMode, { path: '/settings/dark-mode', icon: 'darkMode' })

links.set(SettingsNames.interface, {
  path: '/settings/interface',
  icon: 'photoM',
  roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  features: [],
  subLinks: [
    {
      name: 'aSideMenuSettings',
      path: '/settings/interface/aside-menu-settings',
      component: ASideMenuSettings,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER],
    },
    {
      name: 'languageSettings',
      path: '/settings/interface/language-settings',
      component: LanguageSettings,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER],
    },
    {
      name: 'themeSettings',
      path: '/settings/interface/theme-settings',
      component: ThemeSettings,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUPPORT],
    },
    {
      name: 'chatSettings',
      path: '/settings/interface/chat-settings',
      component: ChatSettings,
      isFeatureEnabled: ref(true),
      roles: [UserRoles.SUBSCRIBER],
    },
  ],
})

links.set(SettingsNames.about, {
  path: '/settings/about',
  icon: 'infoM',
  roles: [UserRoles.SUBSCRIBER, UserRoles.SUPPORT],
  features: [],
})
